// HENDELSESLOGG — «hva har skjedd, nyeste først, hvor gammelt er det».
//
// FUNN 06/07/24: en innsendt rapport forsvant. Den ble lagt på kartet og i
// meldingslista til venstre, men ingenting i UI-et sa «dette ble sendt nå», og
// PERSTAT/LOGSTAT/RFI havnet bare inne i sine egne paneler — som er lukket
// mesteparten av tida. Høyrepanelet, den eneste flata som ALLTID står åpen,
// viste «Ingen objekter ennå».
//
// Loggen er derfor ikke en ny datakilde: den er ett sted der alt som ALLEREDE
// skjer blir synlig i tidsrekkefølge. Rene funksjoner her, DOM i `shell.ts`.
//
// Alderen er med fordi et situasjonsbilde uten alder er en påstand uten
// holdbarhetsdato: «AMMO RØD» er en helt annen melding hvis den er fra i går.
//
// ── HASTEGRAD (UX-GJENNOMGANG 2026-08-18, FUNN F8) ─────────────────────────
// «En CONTACT/TIC eller MEDEVAC ser i loggen ut som en hvilken som helst
// chatlinje. Kritisk og rutine er visuelt likestilt. Øyet får ingen hjelp til å
// prioritere.» Funnet er verifisert: det fantes ingen presedens noe sted i denne
// fila, bare et `farge?`-felt som ble matet fra CASE-STATUS.
//
// ⚠ RAPPORTEN BER OM «farge, plassering øverst og eget varsel». Farge og eget
// bånd øverst gjør vi. Å SORTERE hele loggen etter hastegrad gjør vi IKKE, og
// det er en bevisst avvisning: loggen skal svare på «hva skjedde i timen som
// gikk», og en liste der en 20 minutter gammel MEDEVAC står over en CONTACT som
// kom for tolv sekunder siden er ikke lenger en tidslinje. Da har vi løst F8 ved
// å ødelegge det loggen var til for.
//
// Derfor to soner, hver ærlig for seg:
//   • `hasteBand()` — et kort bånd øverst med det som haster, nyeste først.
//   • `sorterHendelser()` — hele loggen, STRENGT kronologisk, med hastegraden
//     som fargestripe på radkanten.
// En hendelse står i BEGGE. Det er ikke duplisering ved uhell: båndet svarer
// «ikke mist dette», lista svarer «når kom det». Overskriften i båndet sier
// eksplisitt at radene også står i lista under, så ingen tror det er to saker.
import type { PrecedenceId } from '@skade/contracts';

/** Hva slags hendelse — styrer prikkfarge og etikett i lista. */
export type HendelseType =
  | 'rapport' // stridsmelding sendt/mottatt
  /**
   * Ordre gitt (fempunktsordre / IRFMI fra `ui/ordre.ts`).
   *
   * ⚠ LAGT TIL 2026-08-18, UX-funn **F5**: «loggen viser noen chatmeldinger, men
   * ikke stridsmeldinger, ordrer og deteksjoner i én felles tidslinje».
   * Verifisert i koden: stridsmeldinger og deteksjoner LÅ der allerede
   * (`rapport`/`status` fra `addReport`, `varsel` fra varselstrømmen), men ordre
   * gikk gjennom feltmelding-veien og fikk `melding`. En forflytningsordre til et
   * lag så altså ut som en chatlinje. Egen type, fordi en ORDRE er en direktiv —
   * det eneste i loggen som pålegger noen å gjøre noe.
   */
  | 'ordre'
  | 'status' // statusendring på et case
  | 'melding' // feltmelding / PTT
  | 'perstat' // tap registrert
  | 'logstat' // forsyningsstatus / anmodning
  | 'rfi' // informasjonsbehov
  | 'varsel' // alarm/deteksjon/112
  | 'autorisasjon'; // myndighet delegert/trukket, ild frigitt — sporet ER kontrollen

export interface Hendelse {
  id: string;
  /** Når hendelsen skjedde (ms). Alderen regnes mot tida som sendes inn. */
  tsMs: number;
  type: HendelseType;
  /** Én linje: hva skjedde. */
  tittel: string;
  /** Kort undertekst: hvem/hvor/status. */
  detalj: string;
  /** Eksplisitt prikkfarge (f.eks. status- eller meldingstypefarge). */
  farge?: string;
  /**
   * Er hendelsen FORELDET — grå, men fortsatt i lista?
   *
   * ── ⚠ EIERBESLUTNING 2026-08-18 ─────────────────────────────────────────────
   *   «I skade legges det i tidslinja, blir grått etter et døgn.»
   *
   * Gjelder åpne kilder (`grunnbilde.ts`), der `foreldetMs` og `levetidMs` er TO
   * ulike tall: grå etter et døgn, borte etter en uke.
   *
   * ⚠ GRÅ BETYR «KILDEN HAR VÆRT STILLE», IKKE «SAKEN ER OVER». En savnetsak eller
   * en stengt vei kan vare i dager. Leser en operatør grått som «håndtert», har
   * gråfargen gjort skade i stedet for å hjelpe — derfor bærer `detalj` teksten fra
   * `foreldelsesTekst()`, som sier nettopp det.
   */
  foreldet?: boolean;
  /**
   * Militær presedens (FLASH/IMMEDIATE/PRIORITY/ROUTINE).
   *
   * Valgfri med vilje: en hendelse UTEN hastegrad skal se ut som en hendelse
   * uten hastegrad, ikke som ROUTINE. «Vi vet ikke hvor mye dette haster» og
   * «dette haster minst» er to forskjellige påstander, og bare den andre er en
   * påstand vi har grunnlag for.
   */
  hastegrad?: PrecedenceId;
  /** Klikk → gå til objektet/tråden. Uten den er raden bare informasjon. */
  onClick?: () => void;
}

/** Etikett per type — vises som prefiks i undertekst. Norsk, som resten av UI-et. */
export const HENDELSE_ETIKETT: Record<HendelseType, string> = {
  rapport: 'MELDING',
  ordre: 'ORDRE',
  status: 'STATUS',
  melding: 'FELT',
  perstat: 'PERSTAT',
  logstat: 'LOGSTAT',
  rfi: 'RFI',
  varsel: 'VARSEL',
  autorisasjon: 'AUTORISASJON',
};

/**
 * CSS-klasse for hastegrads-stripa på radkanten. Fargene bor i `style.css` som
 * tokens — ikke `var(--token, #fallback)` her, for fallbacken er en hardkodet
 * farge i forkledning (designsystemet UX-022).
 *
 * ⚠ ROUTINE GIR INGEN STRIPE. At stripa i det hele tatt er der ER signalet; en
 * stripe på hver eneste rad ville vært like flatt som ingen stripe.
 *
 * ⚠ FLASH BRUKER `--danger`, ALDRI `--aff-hostile`. «Rød=hostil er hellig»
 * (designsystemet §1) handler om TILHØRIGHETSFARGENE på markører og prikker.
 * Stripa ligger på radkanten, aldri på type-prikken, så de to rødtonene kan ikke
 * bytte plass i øyet: prikken sier hva slags hendelse, kanten sier hvor mye det
 * haster.
 */
export function hastegradKlasse(h: Hendelse): string {
  return h.hastegrad ? `haste-${h.hastegrad}` : '';
}

/**
 * Hastegrader som kvalifiserer til båndet øverst.
 *
 * PRIORITY er holdt UTENFOR. Grunnen er at `REPORT_PRECEDENCE` gir `priority`
 * til fri stridsmelding (MELDING) — den mest brukte meldingstypen i GNÅ. Ville
 * PRIORITY kommet i båndet, hadde båndet blitt fullt av vanlig trafikk innen
 * minuttet, og et bånd som alltid er fullt er en liste, ikke et varsel.
 */
export const HASTEBAND_NIVAA: readonly PrecedenceId[] = ['flash', 'immediate'];

/**
 * Maks rader i haste-båndet.
 *
 * Tre, fordi båndet skal kunne leses i ett blikk uten å rulle. Blir det flere
 * enn tre samtidig, er tallet i overskriften («+4 flere») den ærlige beskjeden —
 * bedre enn et bånd som vokser nedover og skyver hele loggen ut av skjermen
 * nettopp når det er mest å holde styr på.
 */
export const MAKS_HASTE = 3;

/**
 * Hvor lenge noe får stå i haste-båndet: 60 minutter.
 *
 * ⚠ IKKE ET DOKTRINÆRT SVARTIDSKRAV. `precedence.ts` forbyr eksplisitt å dikte
 * opp tidsfrister for kodeordene, og dette er ikke en frist: det er hvor lenge
 * en rad er verdt en egen plass på skjermen. Én time er valgt fordi loggen selv
 * er beskrevet som «timen som gikk». Hendelsen forsvinner ikke — den blir stående
 * i den kronologiske lista, som er der historikken hører hjemme.
 */
export const HASTE_VINDU_MS = 60 * 60_000;

export interface Hasteband {
  /** Rader å vise, nyeste først. */
  vis: Hendelse[];
  /** Hvor mange som kvalifiserte, totalt (inkludert de som ikke vises). */
  totalt: number;
  /** Hvor mange som ikke fikk plass. */
  skjult: number;
}

/**
 * Plukk ut det som haster, nyeste først.
 *
 * ⚠ FILTRERER PÅ TID OGSÅ: en IMMEDIATE fra i går er historie, ikke et varsel,
 * og skal ikke stå og lyse over dagens trafikk. Vinduet sendes INN, så flata kan
 * velge (og testen kan sette det), i stedet for at fila leser klokka selv.
 */
export function hasteBand(
  inn: Hendelse[],
  naaMs: number,
  vinduMs = HASTE_VINDU_MS,
  grense = MAKS_HASTE,
): Hasteband {
  const kval = inn
    .filter((h) => h.hastegrad && HASTEBAND_NIVAA.includes(h.hastegrad))
    .filter((h) => naaMs - h.tsMs <= vinduMs)
    .sort((a, b) => (b.tsMs - a.tsMs) || a.id.localeCompare(b.id));
  const vis = kval.slice(0, grense);
  return { vis, totalt: kval.length, skjult: kval.length - vis.length };
}


/**
 * Overskrift til båndet — sier hva sonen ER, og at radene også står i lista
 * under.
 *
 * Uten den siste setningen ville en operatør sett samme MEDEVAC to steder og
 * trodd det var to meldinger. To hendelser er en helt annen situasjon enn én.
 */
export function hastebandTekst(b: Hasteband): string {
  if (!b.totalt) return '';
  const hale = b.skjult ? ` · +${b.skjult} flere i lista` : '';
  return `HASTER (${b.totalt})${hale} — står også i lista under`;
}

/**
 * Hvor mange hendelser loggen holder. Loggen er en INNBOKS, ikke et arkiv:
 * historikken finnes i meldingslista, tidsmaskinen og hubben. 60 rader er nok
 * til å dekke et skift-bytte, og lite nok til at DOM-en ikke vokser i det
 * uendelige på en maskin som står på i døgn.
 */
export const MAKS_HENDELSER = 60;

/**
 * Nyeste først, kappet. Stabil på id ved identisk tidsstempel (samme batch).
 *
 * ⚠ SORTERER IKKE PÅ HASTEGRAD, og skal ikke gjøre det. Se filhodet: en logg som
 * omrokerer på presedens er ikke lenger en tidslinje. Hastegraden vises som
 * stripe (`hastegradKlasse`) og som eget bånd (`hasteBand`).
 */
export function sorterHendelser(inn: Hendelse[], grense = MAKS_HENDELSER): Hendelse[] {
  return [...inn]
    .sort((a, b) => (b.tsMs - a.tsMs) || a.id.localeCompare(b.id))
    .slice(0, grense);
}

/**
 * Alder som kort tekst: «nå», «4 min», «2 t 15 min», «3 d».
 *
 * Grovere jo eldre hendelsen er — sekundpresisjon på noe som er tre dager gammelt
 * er støy. Negativ alder (klokkeskeiv mellom to noder) vises som «nå», ikke som
 * «-3 min», for et framtidig tidsstempel er en feil i klokka, ikke informasjon
 * til operatøren.
 */
export function alderTekst(naaMs: number, tsMs: number): string {
  const s = Math.floor((naaMs - tsMs) / 1000);
  if (!Number.isFinite(s) || s < 30) return 'nå';
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86_400) {
    const t = Math.floor(s / 3600);
    const min = Math.floor((s % 3600) / 60);
    return min ? `${t} t ${min} min` : `${t} t`;
  }
  return `${Math.floor(s / 86_400)} d`;
}
